import { useMemo, useState } from "react";
import { Gauge, ArrowRight, RefreshCcw } from "lucide-react";
import Reveal from "@/components/Reveal";

const BUDGETS = [
  { label: "Under ₹25 Lakhs", value: 10 },
  { label: "₹25L – ₹1 Crore", value: 25 },
  { label: "₹1 Cr – ₹5 Cr", value: 45 },
  { label: "Above ₹5 Cr", value: 60 },
];

const SECTORS = [
  { label: "Education & Skill", value: 15 },
  { label: "Healthcare & Nutrition", value: 12 },
  { label: "Rural Development", value: 10 },
  { label: "Women Empowerment", value: 12 },
  { label: "Environment & Climate", value: 8 },
  { label: "Livelihood", value: 9 },
];

const AGES = [
  { label: "Less than 3 years", value: 5 },
  { label: "3 – 5 years", value: 10 },
  { label: "5+ years", value: 18 },
];

const TARGETS = [
  { label: "₹10L – ₹50L", value: 15, plan: "CSR Launchpad", months: "2 – 4 months" },
  { label: "₹50L – ₹2 Cr", value: 8, plan: "Scale & Growth", months: "3 – 6 months" },
  { label: "₹2 Cr+", value: 3, plan: "Institutional Enterprise", months: "6 – 12 months" },
];

const selectClass =
  "w-full rounded-xl border border-[#E6E9E6] bg-white px-4 py-3 text-sm font-medium text-slate-800 outline-none transition-colors focus:border-[#0E2A6B] focus:ring-2 focus:ring-[#0E2A6B]/20";

export default function FundingCalculator({ onApply }) {
  const [budget, setBudget] = useState("");
  const [sector, setSector] = useState("");
  const [age, setAge] = useState("");
  const [target, setTarget] = useState("");

  const result = useMemo(() => {
    if (!budget || !sector || !age || !target) return null;
    const score = Math.min(Number(budget) + Number(sector) + Number(age) + Number(target), 97);
    const t = TARGETS.find((x) => String(x.value) === target);
    return { probability: score, plan: t.plan, months: t.months };
  }, [budget, sector, age, target]);

  return (
    <section id="calculator" className="bg-[#0A1A3F] py-20 lg:py-28">
      <div className="mx-auto max-w-7xl px-4 sm:px-6">
        <div className="grid gap-12 lg:grid-cols-2 lg:gap-16">
          <div>
            <p className="font-mono-alt mb-4 text-xs font-semibold tracking-[0.35em] text-[#E8862A]">05 / FUNDING ESTIMATOR</p>
            <span className="inline-flex items-center gap-1.5 rounded-full border border-sky-400/30 bg-sky-400/10 px-3 py-1 text-xs font-semibold uppercase tracking-[0.18em] text-sky-300">
              <Gauge className="h-3.5 w-3.5" />
              Funding Potential Estimator
            </span>
            <h2 className="mt-5 text-2xl font-bold tracking-tight text-white sm:text-3xl lg:text-4xl">
              How fundable is your NGO{" "}
              <span className="font-display italic text-sky-300">right now?</span>
            </h2>
            <p className="mt-4 max-w-md text-base leading-relaxed text-slate-300">
              Answer four quick questions to estimate your corporate match probability, a realistic
              time-to-grant pipeline, and the association tier we'd recommend.
            </p>

            <div className="mt-8 space-y-5">
              <div>
                <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-slate-400">
                  Annual operating budget
                </label>
                <select
                  data-testid="calc-input-operating-budget"
                  value={budget}
                  onChange={(e) => setBudget(e.target.value)}
                  className={selectClass}
                >
                  <option value="">Select budget range</option>
                  {BUDGETS.map((b) => (
                    <option key={b.label} value={b.value}>{b.label}</option>
                  ))}
                </select>
              </div>
              <div className="grid gap-5 sm:grid-cols-2">
                <div>
                  <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-slate-400">
                    Focus sector
                  </label>
                  <select
                    data-testid="calc-select-sector"
                    value={sector}
                    onChange={(e) => setSector(e.target.value)}
                    className={selectClass}
                  >
                    <option value="">Select sector</option>
                    {SECTORS.map((s) => (
                      <option key={s.label} value={s.value}>{s.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-slate-400">
                    Years registered
                  </label>
                  <select
                    data-testid="calc-select-ngo-age"
                    value={age}
                    onChange={(e) => setAge(e.target.value)}
                    className={selectClass}
                  >
                    <option value="">Select age</option>
                    {AGES.map((a) => (
                      <option key={a.label} value={a.value}>{a.label}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div>
                <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-slate-400">
                  Target CSR grant size
                </label>
                <div className="grid grid-cols-3 gap-3">
                  {TARGETS.map((t) => (
                    <button
                      key={t.label}
                      data-testid={`calc-target-${t.value}`}
                      onClick={() => setTarget(String(t.value))}
                      className={`rounded-xl border px-3 py-3 text-sm font-semibold transition-all ${
                        target === String(t.value)
                          ? "border-[#E8862A] bg-[#E8862A]/15 text-[#E8862A]"
                          : "border-white/15 bg-white/5 text-slate-300 hover:border-white/30"
                      }`}
                    >
                      {t.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>

          <Reveal
            data-testid="calc-output-grant-estimate"
            className="flex flex-col justify-center rounded-3xl border border-white/10 bg-white/5 p-8 backdrop-blur"
          >
            {result ? (
              <>
                <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-400">
                  Estimated corporate match probability
                </p>
                <p className="mt-3 font-mono-alt text-6xl font-bold text-white">
                  {result.probability}
                  <span className="text-3xl text-sky-300">%</span>
                </p>
                <div className="mt-4 h-3 overflow-hidden rounded-full bg-white/10">
                  <div
                    className="h-full rounded-full bg-gradient-to-r from-[#0E2A6B] via-sky-400 to-[#E8862A] transition-all duration-700"
                    style={{ width: `${result.probability}%` }}
                  />
                </div>
                <dl className="mt-8 grid grid-cols-2 gap-4">
                  <div className="rounded-2xl bg-white/5 p-4">
                    <dt className="text-xs font-medium uppercase tracking-wider text-slate-400">
                      Time to grant
                    </dt>
                    <dd className="mt-1 text-lg font-bold text-white">{result.months}</dd>
                  </div>
                  <div className="rounded-2xl bg-white/5 p-4">
                    <dt className="text-xs font-medium uppercase tracking-wider text-slate-400">
                      Recommended tier
                    </dt>
                    <dd className="mt-1 text-lg font-bold text-[#E8862A]">{result.plan}</dd>
                  </div>
                </dl>
                <button
                  data-testid="calc-cta-apply-match"
                  onClick={() => onApply(result.plan)}
                  className="mt-8 inline-flex items-center justify-center gap-2 rounded-full bg-[#E8862A] px-7 py-3.5 text-sm font-semibold text-white transition-all hover:-translate-y-0.5 hover:bg-[#C96F1B] hover:shadow-xl"
                >
                  Apply My Match — Request Callback
                  <ArrowRight className="h-4 w-4" />
                </button>
              </>
            ) : (
              <div className="flex flex-col items-center py-10 text-center">
                <RefreshCcw className="h-10 w-10 text-slate-500" />
                <p className="mt-4 max-w-xs text-sm leading-relaxed text-slate-400">
                  Fill in all four fields on the left to see your estimated match probability,
                  time-to-grant, and recommended association tier.
                </p>
              </div>
            )}
            <p className="mt-6 text-center text-xs text-slate-500">
              Indicative estimate only — actual outcomes depend on due diligence and funder cycles.
            </p>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
